import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
type HighlightedTextPart = {
  readonly isMatch: boolean;
  readonly startIndex: number;
  readonly value: string;
};
function getHighlightedTextParts(text: string, query: string): readonly HighlightedTextPart[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return [{
      isMatch: false,
      startIndex: 0,
      value: text
    }];
  }
  const parts: HighlightedTextPart[] = [];
  const lowerQuery = trimmed.toLowerCase();
  let partStartIndex = 0;
  let searchIndex = 0;
  while (searchIndex <= text.length - trimmed.length) {
    const candidate = text.slice(searchIndex, searchIndex + trimmed.length);
    if (candidate.toLowerCase() !== lowerQuery) {
      searchIndex += 1;
      continue;
    }
    if (partStartIndex < searchIndex) {
      parts.push({
        isMatch: false,
        startIndex: partStartIndex,
        value: text.slice(partStartIndex, searchIndex)
      });
    }
    parts.push({
      isMatch: true,
      startIndex: searchIndex,
      value: candidate
    });
    searchIndex += trimmed.length;
    partStartIndex = searchIndex;
  }
  if (partStartIndex < text.length) {
    parts.push({
      isMatch: false,
      startIndex: partStartIndex,
      value: text.slice(partStartIndex)
    });
  }
  return parts.length ? parts : [{
    isMatch: false,
    startIndex: 0,
    value: text
  }];
}
function HighlightedText(t0) {
  const $ = _c(8);
  const {
    text,
    query
  } = t0;
  let t1;
  let t2;
  if ($[0] !== query || $[1] !== text) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const trimmed = query.trim();
      if (!trimmed) {
        let t3;
        if ($[4] !== text) {
          t3 = <>{text}</>;
          $[4] = text;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = t3;
        break bb0;
      }
      const parts = getHighlightedTextParts(text, trimmed);
      t1 = parts.map(_temp);
    }
    $[0] = query;
    $[1] = text;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <>{t1}</>;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp(part) {
  return <span key={`${part.startIndex}-${part.value.length}-${part.isMatch ? "match" : "text"}`} className={part.isMatch ? "tw-text-primary-300" : "tw-text-inherit"}>{part.value}</span>;
}
function CreateWaveGroupSearchResultOption(t0) {
  const $ = _c(22);
  const {
    group,
    index,
    listboxId,
    searchCriteria,
    isActive,
    isSelected,
    onHover,
    onSelect
  } = t0;
  const optionStateClasses = isActive || isSelected ? "tw-bg-iron-800 tw-text-white" : "tw-text-white hover:tw-bg-iron-800";
  const t1 = `${listboxId}-option-${index}`;
  const t2 = `tw-relative tw-flex tw-w-full tw-select-none tw-items-center tw-justify-between tw-rounded-lg tw-px-2 tw-py-2 tw-text-sm tw-font-medium ${optionStateClasses}`;
  let t3;
  if ($[0] !== index || $[1] !== onHover) {
    t3 = () => onHover(index);
    $[0] = index;
    $[1] = onHover;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== group || $[4] !== onSelect) {
    t4 = () => onSelect(group);
    $[3] = group;
    $[4] = onSelect;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== group.name || $[7] !== searchCriteria) {
    t5 = <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-iron-50"><HighlightedText text={group.name} query={searchCriteria} /></span>;
    $[6] = group.name;
    $[7] = searchCriteria;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `@${group.created_by.handle ?? "unknown"}`;
  let t7;
  if ($[9] !== searchCriteria || $[10] !== t6) {
    t7 = <span className="tw-truncate tw-text-xs tw-text-iron-400"><HighlightedText text={t6} query={searchCriteria} /></span>;
    $[9] = searchCriteria;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-1">{t5}{t7}</div>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isSelected || $[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t8) {
    t9 = <li id={t1} role="option" aria-selected={isSelected} className={t2} onMouseEnter={t3} onMouseDown={_temp2} onClick={t4}>{t8}</li>;
    $[15] = isSelected;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp2(event) {
  return event.preventDefault();
}
function CreateWaveGroupSearchResultsList(t0) {
  const $ = _c(20);
  const {
    isFetching,
    suggestions,
    selectedGroupId,
    activeIndex,
    listboxId,
    searchCriteria,
    showNoResults,
    helperText,
    onActiveIndexChange,
    onOptionSelect
  } = t0;
  if (isFetching) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <li role="option" aria-selected={false} aria-disabled="true" className="tw-flex tw-items-center tw-justify-center tw-py-4"><div role="status" aria-live="polite"><span className="tw-sr-only">Loading groups</span><CircleLoader size={CircleLoaderSize.SMALL} /></div></li>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (suggestions.length > 0) {
    let t1;
    if ($[1] !== activeIndex || $[2] !== listboxId || $[3] !== onActiveIndexChange || $[4] !== onOptionSelect || $[5] !== searchCriteria || $[6] !== selectedGroupId || $[7] !== suggestions) {
      let t2;
      if ($[9] !== activeIndex || $[10] !== listboxId || $[11] !== onActiveIndexChange || $[12] !== onOptionSelect || $[13] !== searchCriteria || $[14] !== selectedGroupId) {
        t2 = (group, index) => <CreateWaveGroupSearchResultOption key={group.id} group={group} index={index} listboxId={listboxId} searchCriteria={searchCriteria} isActive={index === activeIndex} isSelected={selectedGroupId === group.id} onHover={onActiveIndexChange} onSelect={onOptionSelect} />;
        $[9] = activeIndex;
        $[10] = listboxId;
        $[11] = onActiveIndexChange;
        $[12] = onOptionSelect;
        $[13] = searchCriteria;
        $[14] = selectedGroupId;
        $[15] = t2;
      } else {
        t2 = $[15];
      }
      t1 = suggestions.map(t2);
      $[1] = activeIndex;
      $[2] = listboxId;
      $[3] = onActiveIndexChange;
      $[4] = onOptionSelect;
      $[5] = searchCriteria;
      $[6] = selectedGroupId;
      $[7] = suggestions;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[16] !== t1) {
      t2 = <>{t1}</>;
      $[16] = t1;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    return t2;
  }
  const t1 = showNoResults ? "No groups found" : helperText;
  let t2;
  if ($[18] !== t1) {
    t2 = <li role="option" aria-selected={false} aria-disabled="true" className="tw-relative tw-w-full tw-select-none tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white">{t1}</li>;
    $[18] = t1;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
}
export default function CreateWaveGroupSearchResults(t0) {
  const $ = _c(15);
  const {
    isOpen,
    disabled,
    listboxId,
    isFetching,
    suggestions,
    selectedGroup,
    activeIndex,
    searchCriteria,
    showNoResults,
    helperText,
    onActiveIndexChange,
    onOptionSelect
  } = t0;
  let t1;
  if ($[0] !== activeIndex || $[1] !== disabled || $[2] !== helperText || $[3] !== isFetching || $[4] !== isOpen || $[5] !== listboxId || $[6] !== onActiveIndexChange || $[7] !== onOptionSelect || $[8] !== searchCriteria || $[9] !== selectedGroup?.id || $[10] !== showNoResults || $[11] !== suggestions) {
    t1 = isOpen && !disabled && <m.div className="tw-absolute tw-z-50 tw-mt-1.5 tw-w-full tw-rounded-lg tw-bg-iron-800 tw-shadow-xl tw-ring-1 tw-ring-black tw-ring-opacity-5" initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.2
    }}><div className="tw-absolute tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-800 tw-shadow-2xl tw-ring-1 tw-ring-white/10"><div className="tw-flow-root tw-max-h-64 tw-overflow-y-auto tw-overflow-x-hidden tw-py-1"><ul id={listboxId} role="listbox" className="tw-mx-0 tw-mb-0 tw-flex tw-list-none tw-flex-col tw-gap-y-1 tw-px-2"><CreateWaveGroupSearchResultsList isFetching={isFetching} suggestions={suggestions} selectedGroupId={selectedGroup?.id ?? null} activeIndex={activeIndex} listboxId={listboxId} searchCriteria={searchCriteria} showNoResults={showNoResults} helperText={helperText} onActiveIndexChange={onActiveIndexChange} onOptionSelect={onOptionSelect} /></ul></div></div></m.div>;
    $[0] = activeIndex;
    $[1] = disabled;
    $[2] = helperText;
    $[3] = isFetching;
    $[4] = isOpen;
    $[5] = listboxId;
    $[6] = onActiveIndexChange;
    $[7] = onOptionSelect;
    $[8] = searchCriteria;
    $[9] = selectedGroup?.id;
    $[10] = showNoResults;
    $[11] = suggestions;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <LazyMotion features={domAnimation}><AnimatePresence mode="wait" initial={false}>{t1}</AnimatePresence></LazyMotion>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}